import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertTriangle, Sparkles, ArrowRight, Dumbbell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SharedExercisePage = () => {
  const { token } = useParams();
  const [exercise, setExercise] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/tracking/custom-exercises/shared/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setExercise(d.exercise))
      .catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="shared-exercise-error">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Cet exercice n'existe pas ou n'est plus disponible.</p>
          <Link to="/" className="text-primary font-semibold hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="shared-exercise-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="shared-exercise-page">
      <div className="relative h-64 sm:h-96 bg-zinc-900">
        {exercise.media_url && (
          exercise.media_type === 'video' ? (
            <video src={`${BACKEND_URL}${exercise.media_url}`} className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline />
          ) : (
            <img src={`${BACKEND_URL}${exercise.media_url}`} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-black/40 to-black/20" />
        <div className="absolute bottom-0 left-0 right-0 p-6 max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-primary/80 text-white mb-3"><Sparkles className="w-3 h-3" /> Exercice créé par un membre Body-Up</span>
          <h1 className="text-white text-2xl sm:text-4xl font-black leading-tight">{exercise.name}</h1>
          <p className="text-sm text-zinc-300 mt-2">{[exercise.muscle_group, exercise.equipment].filter(Boolean).join(' · ')}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {exercise.execution && (
          <div className="mb-8">
            <h2 className="text-base font-bold text-white mb-3 flex items-center gap-1.5"><Dumbbell className="w-4 h-4 text-primary" /> Exécution</h2>
            <p className="text-sm text-zinc-300 whitespace-pre-line">{exercise.execution}</p>
          </div>
        )}

        {exercise.tips?.length > 0 && (
          <div className="mb-10">
            <h2 className="text-base font-bold text-white mb-3">Conseils pro</h2>
            <ul className="space-y-2">
              {exercise.tips.map((t, i) => (
                <li key={i} className="flex gap-2 text-sm text-zinc-300">
                  <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-pink-500/10 border border-primary/30 p-6 text-center">
          <p className="text-white font-bold mb-1">Créez vos propres exercices personnalisés</p>
          <p className="text-sm text-zinc-400 mb-4">Body-Up génère un texte d'exécution puis une image ou vidéo IA sur-mesure pour chacun de vos exercices.</p>
          <Link to="/offres" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform" data-testid="shared-exercise-cta">
            Découvrir Body-Up <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SharedExercisePage;
